import React from 'react';

import { withRouter, RouteComponentProps } from 'react-router-dom';
import { connect } from 'react-redux';

import { ICarsDetailsState, ICarsReviewBookState } from '@store/slices';
import { RootState } from '@share/utils';

import { CarsInfo } from '../cars-info';
import { CarsLocations } from '../cars-location';
import { CarsPolicies } from '../cars-policies';
import { CarsExtras } from '../cars-extras';

import { CarsPayment } from '../cars-payment';
import { CarsDisclaimers } from '../cars-disclaimers';
import { FormInstance } from 'antd/lib/form';
import { DriverInformation } from '../cars-driver-information';
import { CarsConfirmationEmail } from '../cars-confirmation-email';

import { CarsDetailsAnchors } from '../cars-details-anchors';

import './style.scss';
import { FormattedMessage } from 'react-intl';
import { isEmpty } from 'lodash';

interface IMapStateToProps {
  carsDetailsStore: ICarsDetailsState;
  carsReviewBookStore: ICarsReviewBookState;
}

interface IProps extends IMapStateToProps, RouteComponentProps {
  dealId: number;
  backHomeType: string;

  isCheckout: boolean;

  overviewRef: React.RefObject<HTMLDivElement>;
  paymentRef: React.RefObject<HTMLDivElement>;
  driverRef: React.RefObject<HTMLDivElement>;
  emailConfirmationRef: React.RefObject<HTMLDivElement>;
  billingFormRef: React.RefObject<FormInstance>;
  reserveRef: React.RefObject<HTMLDivElement>;
  locationRef: React.RefObject<HTMLDivElement>;
  policiesRef: React.RefObject<HTMLDivElement>;
  extrasRef: React.RefObject<HTMLDivElement>;

  paymentFormRef: React.RefObject<FormInstance>;
  driverFormRef: React.RefObject<FormInstance>;
  emailFormRef: React.RefObject<FormInstance>;
}

export interface IButtonsComplete {
  driverForm: boolean;
  paymentForm: boolean;
  emailForm: boolean;
  billingForm: boolean;
}

interface IMultiplePayments {
  amazonPay: boolean;
  applePay: boolean;
  googlePay: boolean;
  link: boolean;
  paypal: boolean;
}

interface IState {
  isButtonCompleteHidden: IButtonsComplete;
  multiplePayments: IMultiplePayments;
  paymentSelected: string;
  scrollToPaymentSelect: boolean;
  ifEmailChanging: string | undefined;
}

export interface ButtonStripeComplete {
  driverForm: boolean,
  billingForm: boolean,
  emailForm: boolean
}

class CarsDetailsComponent extends React.Component<IProps> {

  state: IState = {
    isButtonCompleteHidden: {
      driverForm: false,
      paymentForm: false,
      emailForm: false,
      billingForm: false
    },
    multiplePayments: {
      amazonPay: false,
      applePay: false,
      googlePay: false,
      link: false,
      paypal: false
    },
    paymentSelected: '',
    scrollToPaymentSelect: false,
    ifEmailChanging: undefined
  };

  render(): React.ReactNode {
    const { carsDetailsStore, carsReviewBookStore, isCheckout, paymentFormRef, driverFormRef, emailFormRef, billingFormRef, driverRef } = this.props;
    const { car } = carsDetailsStore;
    const { bookingComplete } = carsReviewBookStore;

    if (bookingComplete) {
      return null;
    }

    const isCardPayment = !isEmpty(this.state.paymentSelected) && this.state.paymentSelected === 'card';
    const isOtherPayment = !isEmpty(this.state.paymentSelected) && this.state.paymentSelected !== 'card';
    const isEmailChanging = (ifEmailChanging: string | undefined) => this.setState({ ifEmailChanging })

    const scrollToPaymentSelectDisabled = () => this.setState({ scrollToPaymentSelect: false });
    const scrollToPaymentSelectEnabled = () => this.setState({ scrollToPaymentSelect: true });

    const handleDriverFormValidation = (driverForm: boolean): void => {
      this.setState((prevState: IState) => ({
        ...prevState,
        isButtonCompleteHidden: {
          ...prevState.isButtonCompleteHidden,
          driverForm,
        },
      }));
    };

    const handleBillingFormValidation = (billingForm: boolean): void => {
      this.setState((prevState: IState) => ({
        ...prevState,
        isButtonCompleteHidden: {
          ...prevState.isButtonCompleteHidden,
          billingForm,
        },
      }));
    };

    const setPaymentSelected = (payment: string) => {
      this.setState((prevState: IState) => ({
        ...prevState,
        paymentSelected: payment,
      }));
    };

    const handlePaymentFormValidation = (paymentForm: boolean): void => {
      this.setState((prevState: IState) => ({
        ...prevState,
        isButtonCompleteHidden: {
          ...prevState.isButtonCompleteHidden,
          paymentForm,
        },
      }));
    };
    
    const handleEmailFormValidation = (emailForm: boolean): void => {
      this.setState((prevState: IState) => ({
        ...prevState,
        isButtonCompleteHidden: {
          ...prevState.isButtonCompleteHidden,
          emailForm,
        },
      }));
    };

    const setMultiplePayments = (payments: any) => {
      this.setState((prevState: IState) => ({
        ...prevState,
        multiplePayments: {
          ...payments
        },
      }));
    };

    // Define keysToValidate with a type assertion
    const keysToValidate: Array<keyof IButtonsComplete> =
      isCardPayment
        ? ['driverForm', 'emailForm', 'paymentForm'] // If 'card', include include paymentForm
        : ['driverForm', 'emailForm', 'billingForm']; // If not 'card', include billingForm

    const VALIDATE_STRIPE_BTNS = !isEmpty(this.state.paymentSelected) ? (keysToValidate.every(
      (key) => this.state.isButtonCompleteHidden[key] === true)) : false;

    return (
      <>
        <div className="card shadow">
          {/* <div className="card-header border-bottom p-4">
            <h4 className="card-title mb-0">
              <i className="bi bi-people me-2"></i>
            </h4>
          </div> */}
          <div className="card-body p-4">
            <CarsDetailsAnchors
              overviewRef={this.props.overviewRef}
              paymentRef={this.props.paymentRef}
              driverRef={this.props.driverRef}
              emailConfirmationRef={this.props.emailConfirmationRef}
              reserveRef={this.props.reserveRef}
              locationRef={this.props.locationRef}
              policiesRef={this.props.policiesRef}
              extrasRef={this.props.extrasRef}
              isCheckout={isCheckout}
            />

            <div ref={this.props.overviewRef} />
            <CarsInfo car={car} />
          </div>
        </div>

        {isCheckout ? (
          <>
            <div className="card shadow">
              <div className="card-header border-bottom p-4">
                <h4 className="card-title mb-0">
                  <i className="bi bi-wallet me-2"></i><FormattedMessage id="tab.driver" />
                </h4>
              </div>
              <div className="card-body p-4">
                <div ref={driverRef} />
                <DriverInformation
                  formRef={driverFormRef}
                  car={car}
                  handleDriverFormValidation={handleDriverFormValidation}
                  emailRef={emailFormRef}
                  isEmailChanging={isEmailChanging}
                />
              </div>
            </div>

            <div className="card shadow">
              <div className="card-header border-bottom p-4">
                <h4 className="card-title mb-0">
                  <i className="bi bi-wallet me-2"></i>Payment
                </h4>
              </div>
              <div className="card-body p-4">
                <div ref={this.props.paymentRef} />
                <CarsPayment
                  formRef={paymentFormRef}
                  car={car}
                  handleBillingFormValidation={handleBillingFormValidation}
                  multiplePayments={this.state.multiplePayments}
                  setPaymentSelected={setPaymentSelected}
                  paymentSelected={this.state.paymentSelected}
                  handlePaymentFormValidation={handlePaymentFormValidation}
                  scrollToPaymentSelect={this.state.scrollToPaymentSelect}
                  scrollToPaymentSelectDisabled={scrollToPaymentSelectDisabled}
                  billingRef={billingFormRef}
                />

                <div ref={this.props.emailConfirmationRef} />
                <CarsConfirmationEmail
                  formRef={this.props.emailFormRef}
                  handleEmailFormValidation={handleEmailFormValidation}
                  ifEmailChanging={this.state.ifEmailChanging}
                />

                <div ref={this.props.reserveRef} />
                <CarsDisclaimers
                  driverFormRef={driverFormRef}
                  paymentFormRef={paymentFormRef}
                  emailFormRef={emailFormRef}
                  isButtonCompleteHidden={this.state.isButtonCompleteHidden}
                  setMultiplePayments={setMultiplePayments}
                  paymentSelected={this.state.paymentSelected}
                  validateStripeBtns={VALIDATE_STRIPE_BTNS}
                  scrollToPaymentSelectDisabled={scrollToPaymentSelectDisabled}
                  scrollToPaymentSelectEnabled={scrollToPaymentSelectEnabled}
                  billingRef={billingFormRef}
                />
              </div>
            </div>
          </>) : (
          <>
            <div className="card shadow">
              <div className="card-header border-bottom p-4">
                <h4 className="card-title mb-0">
                  <i className="bi bi-geo-alt me-2"></i><FormattedMessage id="cars.location" />
                </h4>
              </div>
              <div className="card-body p-4">
                <div ref={this.props.locationRef} />
                <CarsLocations car={car} />
              </div>
            </div>

            <div className="card shadow">
              <div className="card-header border-bottom p-4">
                <h4 className="card-title mb-0">
                  <i className="bi bi-shield-check me-2"></i><FormattedMessage id="cars.policies" />
                </h4>
              </div>
              <div className="card-body p-4">
                <div ref={this.props.policiesRef} />
                <CarsPolicies car={car} />

                <CarsExtras display={!isCheckout} extrasRef={this.props.extrasRef} />
              </div>
            </div>
          </>)
        }
      </>
    );
  }
}

const mapStateToProps = (state: RootState): IMapStateToProps => {
  return {
    carsDetailsStore: state.carsDetailsStore,
    carsReviewBookStore: state.carsReviewBookStore,
  };
};

export const CarsDetails = connect(mapStateToProps)(withRouter(CarsDetailsComponent));

